import { Injectable } from '@angular/core';
import type { fhirR4 } from '@smile-cdr/fhirts';
import { PaNeededCode, PaNeededExtension } from '../models/dtr-types';

/**
 * Readers for the CRD `ext-coverage-information` extension:
 * https://hl7.org/fhir/us/davinci-crd/StructureDefinition-ext-coverage-information.html
 *
 * The extension is repeatable — an order carries one occurrence per
 * coverage/payer that responded — and each occurrence carries its own nested
 * extensions. Every accessor here therefore scans all occurrences rather than
 * assuming the payer of interest answered first.
 */
@Injectable({
  providedIn: 'root',
})
export class CoverageInformationService {
  static readonly coverageExtensionUrl =
    'http://hl7.org/fhir/us/davinci-crd/StructureDefinition/ext-coverage-information';
  static readonly paNeededExtensionUrl = 'pa-needed';
  static readonly authNeededValueCode: PaNeededCode = 'auth-needed';
  static readonly coverageAssertionIdExtensionUrl = 'coverage-assertion-id';

  /** Every `ext-coverage-information` occurrence on the order, in document order. */
  static getPAExtensions(
    order: fhirR4.Resource | undefined,
  ): fhirR4.Extension[] {
    return (
      order?.['extension']?.filter(
        (ext) => ext.url === CoverageInformationService.coverageExtensionUrl,
      ) ?? []
    );
  }

  /** The DTR questionnaire the payer wants filled in, or `fallback` if none. */
  static getCanonicalUrl(
    order: fhirR4.Resource | undefined,
    fallback?: string,
  ): string | undefined {
    return (
      CoverageInformationService.findNestedExtension(order, 'questionnaire')
        ?.valueCanonical ?? fallback
    );
  }

  /** The payer's prior-auth determination for the order. */
  static getPANeeded(
    order: fhirR4.Resource | undefined,
  ): PaNeededExtension | undefined {
    return CoverageInformationService.findNestedExtension(
      order,
      CoverageInformationService.paNeededExtensionUrl,
    ) as PaNeededExtension | undefined;
  }

  /**
   * The assertion id DTR echoes back as the `context` launch parameter.
   *
   * It is only sent for an occurrence whose `pa-needed` is `auth-needed` — that
   * payer is the one requiring prior auth, so its assertion is the one DTR is
   * launched against. Occurrences deciding `no-auth` or `conditional` are skipped
   * even when they carry an id. This gate is narrower than DTR
   * <a href="https://build.fhir.org/ig/HL7/davinci-dtr/en/confdetails.html#ci-c-spec-108">§spec-108</a>,
   * which keys off `info-needed`; EPAS-82 pinned it to `auth-needed` to match the
   * payer responses observed in test. Omitting the id when unavailable stays
   * consistent with
   * <a href="https://build.fhir.org/ig/HL7/davinci-dtr/en/confdetails.html#ci-c-spec-107">§spec-107</a>.
   *
   * The first occurrence that both requires auth and carries an id wins.
   */
  static getCoverageAssertionId(
    order: fhirR4.Resource | undefined,
  ): string | undefined {
    for (const paExtension of CoverageInformationService.getPAExtensions(
      order,
    )) {
      const nested = paExtension.extension ?? [];
      const authNeeded = nested.some(
        (ext) =>
          ext.url === CoverageInformationService.paNeededExtensionUrl &&
          ext.valueCode === CoverageInformationService.authNeededValueCode,
      );
      if (!authNeeded) {
        continue;
      }
      const assertionId = nested.find(
        (ext) =>
          ext.url ===
          CoverageInformationService.coverageAssertionIdExtensionUrl,
      )?.valueString;
      if (assertionId) {
        return assertionId;
      }
    }
    return undefined;
  }

  private static findNestedExtension(
    order: fhirR4.Resource | undefined,
    url: string,
  ): fhirR4.Extension | undefined {
    for (const paExtension of CoverageInformationService.getPAExtensions(
      order,
    )) {
      const found = paExtension.extension?.find((ext) => ext.url === url);
      if (found) {
        return found;
      }
    }
    return undefined;
  }
}
